import React, { useState } from "react"
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, MenuItem, Paper, Snackbar, Stack,
    Switch, TextField, Tooltip, Typography
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import EditIcon from "@mui/icons-material/Edit"
import LinkIcon from "@mui/icons-material/Link"
import AddIcon from "@mui/icons-material/Add"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { format } from "date-fns"
import { asDialogData, getDateInServerTimeZone } from "../../../core/utils/utils"
import {
    useGetAllCouponsQuery,
    useGetAllExperienceCategoriesQuery,
    useCreateCouponMutation,
    useUpdateCouponMutation,
    useToggleCouponActiveMutation,
} from "../../../core/api/experience-api"
import ResponseDialog from "../../ResponseDialog"

function fmt(s) {
    if (!s) return "—"
    try { return format(getDateInServerTimeZone(s), "dd.MM.yyyy HH:mm") } catch { return s }
}

// Active = explicit flag AND inside its valid period.
function isLive(coupon) {
    if (!coupon.active) return false
    const now = new Date()
    return new Date(coupon.validFrom) <= now && now <= new Date(coupon.validTo)
}

export default function CouponsAdmin() {
    const { data: coupons = [], isLoading } = useGetAllCouponsQuery()
    const { data: categories = [] } = useGetAllExperienceCategoriesQuery()
    const [createCoupon, createResp] = useCreateCouponMutation()
    const [updateCoupon, updateResp] = useUpdateCouponMutation()
    const [toggleActive] = useToggleCouponActiveMutation()

    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({
        alias: "", title: "", description: "", xpAmount: 50, categoryId: "",
        validFrom: new Date(), validTo: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
        active: true,
    })
    const [respOpen, setRespOpen] = useState(false)
    const [actionResp, setActionResp] = useState({})
    const [copyToast, setCopyToast] = useState(null)

    function openNew() {
        setEditing({})
        setForm({
            alias: "", title: "", description: "", xpAmount: 50, categoryId: "",
            validFrom: new Date(), validTo: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            active: true,
        })
    }
    function openEdit(row) {
        setEditing(row)
        setForm({
            alias: row.alias, title: row.title, description: row.description || "",
            xpAmount: row.xpAmount,
            categoryId: row.categoryId || "",
            validFrom: row.validFrom ? new Date(row.validFrom) : new Date(),
            validTo: row.validTo ? new Date(row.validTo) : new Date(),
            active: !!row.active,
        })
    }
    function close() { setEditing(null) }

    async function save() {
        const body = {
            alias: form.alias.trim(),
            title: form.title.trim(),
            description: form.description,
            xpAmount: parseInt(form.xpAmount, 10) || 0,
            categoryId: form.categoryId || "",
            validFrom: form.validFrom?.toISOString?.() ?? form.validFrom,
            validTo: form.validTo?.toISOString?.() ?? form.validTo,
            active: form.active,
        }
        const resp = editing.id
            ? await updateCoupon({ id: editing.id, ...body })
            : await createCoupon(body)
        setActionResp(asDialogData(resp))
        setRespOpen(true)
        if (!resp.error) close()
    }

    async function toggle(row) {
        const resp = await toggleActive({ id: row.id, active: !row.active })
        setActionResp(asDialogData(resp))
        setRespOpen(true)
    }

    function couponLink(alias) {
        return `${window.location.origin}/experience/redeem/${encodeURIComponent(alias)}`
    }

    async function copyLink(alias) {
        const link = couponLink(alias)
        try {
            await navigator.clipboard.writeText(link)
            setCopyToast(`Link copied: ${link}`)
        } catch {
            setCopyToast(link)
        }
    }

    const columns = [
        { field: "alias", headerName: "Alias", width: 160 },
        { field: "title", headerName: "Title", width: 200 },
        { field: "xpAmount", headerName: "XP", width: 80 },
        { field: "categoryName", headerName: "Category", width: 130, renderCell: (p) => p.value || "—" },
        { field: "validFrom", headerName: "Valid from", width: 150, renderCell: (p) => fmt(p.value) },
        { field: "validTo", headerName: "Valid to", width: 150, renderCell: (p) => fmt(p.value) },
        { field: "redemptionCount", headerName: "Redeemed", width: 110 },
        { field: "active", headerName: "Active", width: 100, renderCell: (p) => (
            <Switch
                size="small"
                checked={!!p.value}
                onChange={() => toggle(p.row)}
            />
        )},
        { field: "live", headerName: "Live", width: 80, renderCell: (p) => isLive(p.row)
            ? <Chip size="small" label="live" color="success" />
            : <Chip size="small" label="off" color="default" />
        },
        { field: "actions", headerName: "", width: 110, renderCell: (p) => (
            <Stack direction="row">
                <Tooltip title="Copy redeem link">
                    <IconButton size="small" onClick={() => copyLink(p.row.alias)}>
                        <LinkIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(p.row)}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>
        )},
    ]

    if (isLoading) return <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress /></Box>

    return (
        <Box sx={{ py: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">XP coupons</Typography>
                <Button variant="contained" color="warning" startIcon={<AddIcon />} onClick={openNew}>
                    New coupon
                </Button>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
                Create a coupon with an alias and a validity period. Share the link with participants — each of them can redeem
                the coupon once and receive the specified amount of XP. Coupons cannot be deleted — only deactivated.
            </Alert>
            <Paper sx={{ height: 580 }}>
                <DataGrid
                    rows={coupons}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={!!editing} onClose={close} maxWidth="sm" fullWidth>
                <DialogTitle>{editing?.id ? "Edit coupon" : "New coupon"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Alias"
                            helperText="Lowercase URL-safe identifier, e.g. spring-meetup-2026"
                            value={form.alias}
                            onChange={e => setForm({ ...form, alias: e.target.value })}
                            required
                        />
                        <TextField
                            label="Title"
                            value={form.title}
                            onChange={e => setForm({ ...form, title: e.target.value })}
                            required
                        />
                        <TextField
                            label="Description"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="XP amount"
                            type="number"
                            value={form.xpAmount}
                            onChange={e => setForm({ ...form, xpAmount: e.target.value })}
                            required
                        />
                        <TextField
                            select
                            label="Category"
                            helperText="XP from this coupon will be attributed to the chosen category"
                            value={form.categoryId}
                            onChange={e => setForm({ ...form, categoryId: e.target.value })}
                        >
                            <MenuItem value="">— none (total only) —</MenuItem>
                            {categories.filter(c => c.active !== false).map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DateTimePicker
                                label="Valid from"
                                value={form.validFrom}
                                onChange={(v) => setForm({ ...form, validFrom: v })}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                            <DateTimePicker
                                label="Valid to"
                                value={form.validTo}
                                onChange={(v) => setForm({ ...form, validTo: v })}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Switch
                                checked={!!form.active}
                                onChange={e => setForm({ ...form, active: e.target.checked })}
                            />
                            <Typography>Active</Typography>
                        </Stack>
                        {editing?.alias && (
                            <Alert severity="info">
                                Redeem link: <code>{couponLink(editing.alias)}</code>
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancel</Button>
                    <Button variant="contained" color="warning" onClick={save} disabled={createResp.isLoading || updateResp.isLoading}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <ResponseDialog
                open={respOpen}
                onClose={() => setRespOpen(false)}
                responseData={actionResp}
                label="Coupon"
                information="Saved"
            />
            <Snackbar
                open={!!copyToast}
                autoHideDuration={3500}
                onClose={() => setCopyToast(null)}
                message={copyToast}
            />
        </Box>
    )
}
